import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Plus, Save, Tag, Trash2 } from 'lucide-react';
import {
  AdminCategory,
  createAdminCategory,
  deleteAdminCategory,
  listAdminCategories,
  updateAdminCategory,
} from '../services/categoriesAdminService';

export default function SuperadminCategories() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<AdminCategory[]>([]);
  const [newName, setNewName] = useState('');
  const [newEnglishLabel, setNewEnglishLabel] = useState('');
  const [draftNames, setDraftNames] = useState<Record<number, string>>({});
  const [draftEnglishLabels, setDraftEnglishLabels] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listAdminCategories();
      setItems(rows);
      setDraftNames(Object.fromEntries(rows.map((row) => [row.id, row.name])));
      setDraftEnglishLabels(Object.fromEntries(rows.map((row) => [row.id, row.englishLabel || ''])));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossibile caricare le categorie.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const hasUnsavedChanges = useMemo(
    () => items.some((item) =>
      String(draftNames[item.id] || '').trim() !== String(item.name || '').trim()
      || String(draftEnglishLabels[item.id] || '').trim() !== String(item.englishLabel || '').trim()),
    [items, draftEnglishLabels, draftNames],
  );

  const onCreate = async () => {
    const name = String(newName || '').trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      await createAdminCategory(name, String(newEnglishLabel || '').trim());
      setNewName('');
      setNewEnglishLabel('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Creazione categoria non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  const onSaveAll = async () => {
    const changed = items
      .map((item) => ({
        id: item.id,
        prev: String(item.name || '').trim(),
        next: String(draftNames[item.id] || '').trim(),
        prevEn: String(item.englishLabel || '').trim(),
        nextEn: String(draftEnglishLabels[item.id] || '').trim(),
      }))
      .filter((row) => row.next && (row.next !== row.prev || row.nextEn !== row.prevEn));
    if (changed.length === 0) return;

    setSaving(true);
    setError(null);
    try {
      for (const row of changed) {
        // eslint-disable-next-line no-await-in-loop
        await updateAdminCategory(row.id, row.next, row.nextEn);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aggiornamento categorie non riuscito.');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!window.confirm('Eliminare questa categoria?')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteAdminCategory(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Eliminazione categoria non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Categorie Esperienze</h1>
          <p className="text-sm text-text-secondary">
            Gestione super-admin delle categorie usate da webapp e mobile app.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-sm text-error flex items-center gap-2">
          <AlertCircle size={15} />
          {error}
        </div>
      )}

      <div className="rounded-xl border border-border bg-white p-4 space-y-3">
        <div className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Plus size={15} />
          Nuova categoria
        </div>
        <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
          <input
            className="rounded-lg border border-border px-3 py-2 text-sm"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Es. Borghi storici"
            disabled={saving}
          />
          <input
            className="rounded-lg border border-border px-3 py-2 text-sm"
            value={newEnglishLabel}
            onChange={(e) => setNewEnglishLabel(e.target.value)}
            placeholder="English label, es. Historic villages"
            disabled={saving}
          />
          <button
            type="button"
            onClick={() => void onCreate()}
            disabled={saving || !String(newName || '').trim()}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus size={14} />
            Crea
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-sm font-semibold text-text-primary">Elenco categorie</div>
          <button
            type="button"
            onClick={() => void onSaveAll()}
            disabled={saving || !hasUnsavedChanges}
            className="btn-outline inline-flex items-center gap-2"
          >
            <Save size={14} />
            Salva modifiche
          </button>
        </div>
        {loading ? (
          <p className="text-sm text-text-secondary">Caricamento categorie…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-text-secondary">Nessuna categoria disponibile.</p>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="grid gap-2 rounded-lg border border-border px-3 py-2 md:grid-cols-[20px_1fr_1fr_auto] md:items-center"
              >
                <Tag size={14} className="text-text-secondary shrink-0" />
                <input
                  className="text-sm bg-transparent outline-none"
                  value={draftNames[item.id] ?? item.name}
                  onChange={(e) => setDraftNames((prev) => ({ ...prev, [item.id]: e.target.value }))}
                  disabled={saving}
                  placeholder="Nome IT"
                />
                <input
                  className="text-sm bg-transparent outline-none"
                  value={draftEnglishLabels[item.id] ?? item.englishLabel ?? ''}
                  onChange={(e) => setDraftEnglishLabels((prev) => ({ ...prev, [item.id]: e.target.value }))}
                  disabled={saving}
                  placeholder="English label"
                />
                <button
                  type="button"
                  className="text-error hover:text-error/80"
                  onClick={() => void onDelete(item.id)}
                  disabled={saving}
                  title="Elimina categoria"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
